import type { Params } from "@solidjs/router";
import {
  createConversationRequest,
  newStreamPlaybackRequest,
  useCallOptions,
  useChatClient,
  useChatStreamingClient,
  withBearerToken,
} from "@storytell/cp-client";
import type { ConversationResponse } from "@storytell/cp-client/src/gen/chat/v3/chat_pb";
import { dispatchGlobalEvent } from "@storytell/events";
import type { StreamingChatId, StreamingMessageId, StreamingService } from "@storytell/streaming";
import { createResource } from "solid-js";
import { isServer } from "solid-js/web";
import { createResourceStorage } from "../../lib/createResourceStorage";
import type { ChatPrefetchCache } from "./chatPrefetchCache";
import type { ChatServiceState, SetChatServiceState } from "./chatService";
import type { createChatServiceScope } from "./chatServiceScope";

const RECENT_MESSAGES_COUNT = BigInt(20);

// A payload warmed within this window is still fresh, so opening a just-warmed
// chat serves the cache without a background revalidation.
const REVALIDATE_AFTER_MS = 30 * 1000;

// Recent-window conversation fetch, keeping page size and auth wiring in one place.
export const fetchRecentConversation = (chatId: string, token: string | undefined) =>
  useChatClient().conversation(
    createConversationRequest({ chatId, recentMessagesCount: RECENT_MESSAGES_COUNT }),
    useCallOptions(withBearerToken(token))
  );

// Visibility loss or deletion arrives as a 404 whose stable code only rides in
// the message; Connect maps the HTTP status to "unknown", not NotFound, so
// detection matches the message text.
const isChatNotFoundError = (message: string) => message.includes("chats.get.not.found");

/** What the conversation fetch hands back, and what the SSR storage replays on the client. */
type ConversationLoad =
  | { readonly type: "success"; readonly data: ConversationResponse }
  | { readonly type: "streaming"; readonly data: ConversationResponse }
  | { readonly type: "error"; readonly error: unknown; readonly chatId: StreamingChatId };

export const createChatServiceResource = (deps: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  urlParams: () => Params;
  /** Current project ID from user context */
  projectId: () => string | undefined;
  setChatTitle?: (chatId: StreamingChatId, title: string) => void;
  /** Called when a chat loads without a title so the caller can watch for it */
  onMissingTitle?: (chatId: StreamingChatId) => void;
  /** Record the loaded conversation's resource-visibility and whether the viewer may change it, so the badge/banner/controls can read them for chats outside the sidebar list */
  setConversationVisibility?: (chatId: StreamingChatId, visibility: string, canManageVisibility: boolean) => void;
  /** Record the chat's total message count so the thread knows whether older messages exist */
  setOlderTotal?: (chatId: StreamingChatId, total: number) => void;
  state: ChatServiceState;
  setState: SetChatServiceState;
  stream: StreamingService;
  abortCurrentStream: () => void;
  setCurrentStreamAbortController: (controller: AbortController | undefined) => void;
  /** Relays the frames of the stream behind `controller`, resuming the turn if it goes silent */
  watchStream: <T>(frames: AsyncIterable<T>, controller: AbortController) => AsyncIterable<T>;
  resumeTurn: () => void;
  /** Scope store; sync from last message when conversation loads so prompt bar and scope picker stay in sync */
  scope: ReturnType<typeof createChatServiceScope>;
  /** Raw-response cache for instant hydration of settled chats and warm-on-idle */
  prefetchCache: ChatPrefetchCache;
  /** Record the last chatId whose content was processed into the granular stores */
  setLastHydratedChatId: (chatId: StreamingChatId) => void;
}) => {
  const syncScopeFromLastMessage = (chatId: StreamingChatId) => {
    const chat = deps.stream.state.chats[chatId];
    if (!chat?.messages?.length) {
      return;
    }
    const lastMessageId = chat.messages[chat.messages.length - 1];
    const lastMessage = lastMessageId && deps.stream.state.messages[lastMessageId];
    const scope = lastMessage?.prompt?.scope;
    if (scope) {
      deps.scope.loadScopeFromMessage(scope);
    }
  };

  // allowTitleWatch keeps a background refresh from re-arming a title watch that already ended.
  const applyConversationMetadata = (data: ConversationResponse, allowTitleWatch: boolean) => {
    deps.setConversationVisibility?.(data.chatId as StreamingChatId, data.visibility, data.canManageVisibility);
    deps.setOlderTotal?.(data.chatId as StreamingChatId, Number(data.totalMessageCount));
    const title = data.title?.trim() || "";
    const isPlaceholder = !title || title === "New" || title === "New Chat";
    if (deps.setChatTitle && !isPlaceholder) {
      deps.setChatTitle(data.chatId as StreamingChatId, title);
    } else if (allowTitleWatch && deps.onMissingTitle) {
      deps.onMissingTitle(data.chatId as StreamingChatId);
    }
  };

  const applySettledConversation = (data: ConversationResponse, allowTitleWatch: boolean) => {
    deps.stream.chat.processChatRecording(data, new Set([data.chatId as StreamingChatId]));
    deps.setLastHydratedChatId(data.chatId as StreamingChatId);
    syncScopeFromLastMessage(data.chatId as StreamingChatId);
    applyConversationMetadata(data, allowTitleWatch);
  };

  // Refresh a chat that was just served from cache. Re-processes only when the
  // chat is still the settled one on screen — a stream that started in the
  // meantime owns the granular stores and must not be overwritten. Never throws.
  const revalidateInBackground = async (chatId: string) => {
    try {
      const conversation = await fetchRecentConversation(chatId, deps.authToken());
      if (!conversation.isOk()) {
        // A chat deleted or hidden since we cached it 404s here. Drop the stale
        // entry and, when it's still on screen, surface access-lost so the
        // instant-hydrated content doesn't linger.
        if (isChatNotFoundError(conversation.error.message)) {
          deps.prefetchCache.delete(chatId);
          if (deps.state.chatId === chatId && deps.urlParams().chatId === chatId) {
            deps.setState({ isStreaming: false, chatId: chatId, accessLost: true });
          }
        }
        return;
      }
      const data = conversation.value;
      if (data.streamingState?.activeMessageId) return;

      deps.prefetchCache.set(chatId, data);

      const stillSettledHere =
        deps.state.chatId === chatId && !deps.state.isStreaming && deps.urlParams().chatId === chatId;
      if (!stillSettledHere) return;

      // Replaying this recent-window payload runs processChatRecording, which clears
      // the granular stores, dropping older messages the user has paged in. Refresh
      // metadata only when more is loaded than the payload carries.
      const loadedCount = deps.stream.state.chats[chatId]?.messages.length ?? 0;
      if (loadedCount > data.messages.length) {
        applyConversationMetadata(data, false);
        return;
      }

      applySettledConversation(data, false);
    } catch (error) {
      console.error("chatService: background revalidation failed:", { chatId, error });
    }
  };

  const canFetch = () => {
    if (isServer) return undefined;

    const chatId = deps.urlParams().chatId;
    if (!deps.authReady()) return undefined;

    // Wait for project context to match URL's project before fetching chat.
    // This prevents fetching a chat with the wrong project context when
    // navigating directly to a URL in a different project.
    const urlProjectId = deps.urlParams().projectId;
    const contextProjectId = deps.projectId();
    if (urlProjectId && contextProjectId && urlProjectId !== contextProjectId) {
      return undefined;
    }

    return chatId;
  };

  const storage = createResourceStorage<ConversationLoad>(
    () => undefined,
    (d) => {
      if (!d) return;
      if (d.type === "success") {
        applySettledConversation(d.data, true);
      } else if (d.type === "streaming") {
        applyConversationMetadata(d.data, true);
      } else if (d.type === "error") {
        console.error("chatService: error fetching chat:", { error: d.error });
        const message = (d.error as { message?: string } | undefined)?.message ?? "";
        deps.setState({
          isStreaming: false,
          chatId: d.chatId,
          error: d.error as Error,
          accessLost: isChatNotFoundError(message),
        });
      }
    }
  );

  const [resource] = createResource<ConversationLoad | undefined, string>(
    canFetch,
    async (chatId): Promise<ConversationLoad | undefined> => {
      try {
        if (!chatId) {
          deps.setState({
            isStreaming: false,
            chatId: undefined,
            error: undefined,
            accessLost: false,
          });
          return;
        }

        const isThisChatStreaming = deps.state.isStreaming && deps.state.chatId === chatId;

        // Always update chatId so UI can switch to new chat's content
        deps.setState("chatId", chatId as StreamingChatId);

        if (isThisChatStreaming) {
          return;
        }

        // Instant-hydrate a settled chat from the raw-response cache: skip the
        // round-trip, replay through the storage handler (processChatRecording +
        // side-effects), then revalidate in the background. A cached payload with
        // an active stream must take the live playback path below, never this one.
        const cached = deps.prefetchCache.get(chatId);
        if (cached && !cached.streamingState?.activeMessageId) {
          deps.setState({
            isStreaming: false,
            currentStreamingMessageId: undefined,
            chatId: chatId as StreamingChatId,
            error: undefined,
            accessLost: false,
          });

          const ageMs = deps.prefetchCache.ageMs(chatId) ?? Number.POSITIVE_INFINITY;
          if (ageMs >= REVALIDATE_AFTER_MS) {
            void revalidateInBackground(chatId);
          }

          return { type: "success", data: cached } as const;
        }

        const conversation = await deps.prefetchCache.load(chatId);

        if (conversation.isOk()) {
          const urlProjId = deps.urlParams().projectId;
          if (urlProjId) {
            dispatchGlobalEvent("project-access-ok", { projectId: urlProjId });
          }
          const data = conversation.value;

          if (data.streamingState?.activeMessageId) {
            // Re-check if this chat is already streaming locally. The initial guard (before the await)
            // may have passed, but while we were fetching, a live stream may have started.
            // Don't abort an active local stream just to start playback of the same stream.
            const isNowStreaming = deps.state.isStreaming && deps.state.chatId === chatId;
            if (isNowStreaming) {
              return;
            }

            deps.abortCurrentStream();

            const abortController = new AbortController();
            deps.setCurrentStreamAbortController(abortController);

            const activeMid = data.streamingState.activeMessageId as StreamingMessageId;
            deps.setState({
              isStreaming: true,
              chatId: chatId as StreamingChatId,
              error: undefined,
              accessLost: false,
              currentStreamingMessageId: activeMid,
            });

            // Load existing messages (backend excludes streaming message from blob loading)
            deps.stream.chat.processChatRecording(
              data,
              new Set([chatId as StreamingChatId]),
              data.streamingState.activeMessageId
            );
            deps.setLastHydratedChatId(chatId as StreamingChatId);
            // Sync scope from last message so prompt bar and scope picker agree
            syncScopeFromLastMessage(chatId as StreamingChatId);

            const chatStreamingClient = useChatStreamingClient();
            const playbackStream = chatStreamingClient.streamPlayback(
              newStreamPlaybackRequest(data.streamingState.activeMessageId, chatId, 1.0),
              { bearerToken: deps.authToken(), signal: abortController.signal }
            );

            // A playback that fails or stops short of the turn's end goes through the reconnect
            // path, which retries while the turn runs and loads its final state once it has ended.
            const followPlayback = async (): Promise<void> => {
              const outcome = await deps.stream.chat
                .processStreamingChat(deps.watchStream(playbackStream, abortController), abortController.signal)
                .catch((error: unknown) => {
                  console.error("chatService: playback stream error:", { chatId, error });
                  return "interrupted" as const;
                });
              // Don't touch streaming state if this was an intentional abort —
              // a new stream/reconnection is already handling recovery.
              if (abortController.signal.aborted) return;
              if (outcome === "interrupted") {
                deps.resumeTurn();
                return;
              }
              deps.setCurrentStreamAbortController(undefined);
              deps.setState("isStreaming", false);
            };
            void followPlayback();

            return { type: "streaming", data } as const;
          }

          deps.setState({
            isStreaming: false,
            currentStreamingMessageId: undefined,
            error: undefined,
            accessLost: false,
          });

          if (isServer) {
            storage[1]({ type: "success", data });
          }
          return { type: "success", data } as const;
        }

        console.error("chatService: error fetching chat:", {
          chatId,
          error: conversation.error,
          errorCode: conversation.error.code,
          errorMessage: conversation.error.message,
          errorDetail: conversation.error.details,
        });

        if (conversation.error.isUnavailable() || conversation.error.isUnknown()) {
          dispatchGlobalEvent("connectivity-api-error", {
            error: conversation.error.message,
            code: String(conversation.error.code),
          });
        }

        const errorObj = new Error(
          conversation.error.message ||
            String(conversation.error.details) ||
            String(conversation.error.code || "Unknown error")
        );
        (errorObj as Error & { errorResult?: typeof conversation.error }).errorResult = conversation.error;

        // The project is still reachable, so show the lost-access panel inline
        // rather than the project-level access-denied flow.
        const isChatNotVisible = isChatNotFoundError(conversation.error.message);

        if (!isChatNotVisible) {
          // Signal project no-access for anon/invite flow so they see "sign in" instead of spinner.
          // Include: 403/401, Internal (500), NotFound, Unknown (backend may return 500 + Unknown for "chat not found" / no access).
          // Exclude only isUnavailable — that is clearly network and triggers connectivity-api-error above.
          const urlProjectId = deps.urlParams().projectId;
          const isPermissionDenied = conversation.error.isPermissionDenied();
          const isUnauthenticated = conversation.error.isUnauthenticated();
          const isInternal = conversation.error.isInternal();
          const isNotFound = conversation.error.isNotFound();
          const isUnknown = conversation.error.isUnknown();
          const shouldDispatchAccessDenied =
            urlProjectId && (isPermissionDenied || isUnauthenticated || isInternal || isNotFound || isUnknown);
          if (shouldDispatchAccessDenied) {
            dispatchGlobalEvent("project-access-denied", { projectId: urlProjectId });
          }
        }

        const errorResult = { type: "error" as const, error: conversation.error, chatId: chatId as StreamingChatId };
        if (!isServer) {
          deps.setState({
            isStreaming: false,
            chatId: chatId as StreamingChatId,
            error: errorObj,
            accessLost: isChatNotVisible,
          });
        }
        if (isServer) {
          storage[1](errorResult);
        }
        return errorResult;
      } catch (error) {
        console.error("chatService: error fetching chat:", { chatId, error });
        const errorResult = { type: "error" as const, error, chatId: chatId as StreamingChatId };
        if (!isServer) {
          deps.setState({
            isStreaming: false,
            chatId: chatId as StreamingChatId,
            error: error as Error,
            accessLost: false,
          });
        }
        if (isServer) {
          storage[1](errorResult);
        }
        return errorResult;
      }
    },
    {
      deferStream: true,
      storage: () => storage,
    }
  );

  return resource;
};
